import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FileText, Hash, Scale, AlertCircle, Zap, Store, CircleDollarSign, Languages, Sun, Moon } from 'lucide-react';
import { useTranslation } from '../lib/translations';

const Terms: React.FC = () => {
  const { t, language, setLanguage } = useTranslation();
  const location = useLocation();
  const [isDark, setIsDark] = React.useState(document.documentElement.classList.contains('dark'));

  React.useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDark]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#020617] transition-colors duration-300">
      {/* Floating Navigation Header - Matches Landing Page */}
      <nav className="fixed top-6 left-1/2 -translate-x-1/2 w-[calc(100%-2rem)] md:w-[calc(100%-3rem)] max-w-6xl z-50 glass-card rounded-2xl px-6 h-16 flex items-center justify-between shadow-2xl border-white/40 dark:border-white/5">
        <Link to="/" className="flex items-center gap-3 group">
          <div className="p-2 bg-gradient-to-br from-purple-600 to-indigo-700 rounded-lg shadow-lg group-hover:scale-105 transition-transform">
            <Hash size={18} className="text-white" />
          </div>
          <span className="hidden sm:block text-lg font-black tracking-tight text-slate-900 dark:text-white">SharpNote</span>
        </Link>
        
        <div className="flex items-center gap-4 md:gap-8">
          <Link 
            to="/teachers" 
            className={`flex items-center gap-2 text-[10px] font-black uppercase tracking-widest transition-colors ${location.pathname === '/teachers' ? 'text-purple-600 dark:text-purple-400' : 'text-slate-500 dark:text-slate-400 hover:text-purple-600'}`}
          >
            <Store size={16} />
            {t.nav.marketplace}
          </Link>
          
          <Link 
            to="/pricing" 
            className={`hidden md:flex items-center gap-2 text-[10px] font-black uppercase tracking-widest transition-colors ${location.pathname === '/pricing' ? 'text-purple-600 dark:text-purple-400' : 'text-slate-500 dark:text-slate-400 hover:text-purple-600'}`}
          >
            <CircleDollarSign size={16} />
            {t.nav.pricing}
          </Link>
          
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setLanguage(language === 'en' ? 'fr' : 'en')}
              className="hidden sm:flex items-center gap-2 px-3 py-1.5 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/5 rounded-lg transition-all text-[10px] font-black uppercase tracking-widest"
            >
              <Languages size={16} />
              {language.toUpperCase()}
            </button>
            <button 
              onClick={() => setIsDark(!isDark)}
              className="p-2 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/5 rounded-lg transition-all"
            >
              {isDark ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <Link 
              to="/login" 
              className="px-6 py-2 rounded-lg bg-slate-900 dark:bg-white text-white dark:text-slate-950 font-black text-[10px] uppercase tracking-widest hover:scale-105 transition-all shadow-xl"
            >
              {t.landing.enterApp}
            </Link>
          </div>
        </div>
      </nav>

      <main className="pt-40 pb-20 px-6">
        <div className="max-w-3xl mx-auto space-y-12">
          <div className="space-y-4">
            <div className="inline-flex p-3 bg-indigo-100 dark:bg-indigo-900/20 text-indigo-600 rounded-2xl">
              <Scale size={32} />
            </div>
            <h1 className="text-4xl md:text-5xl font-black text-slate-900 dark:text-white tracking-tighter italic">{t.terms.title}</h1>
            <p className="text-slate-500 font-medium italic">{t.terms.lastUpdated}</p>
          </div>

          <div className="prose prose-slate dark:prose-invert max-w-none space-y-10">
            <section className="space-y-4">
              <h2 className="text-xl font-black uppercase tracking-widest text-slate-900 dark:text-white flex items-center gap-3">
                <Zap size={20} className="text-purple-600" /> {t.terms.acceptance.title}
              </h2>
              <p className="text-slate-600 dark:text-slate-400 leading-relaxed font-medium">
                {t.terms.acceptance.content}
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-black uppercase tracking-widest text-slate-900 dark:text-white flex items-center gap-3">
                <Scale size={20} className="text-indigo-600" /> {t.terms.usage.title}
              </h2>
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/5 p-6 rounded-2xl space-y-4 shadow-sm">
                <p className="text-slate-600 dark:text-slate-400 text-sm font-medium leading-relaxed">
                  {t.terms.usage.desc}
                </p>
                <ul className="space-y-3">
                  {t.terms.usage.points.map((item: string, i: number) => (
                    <li key={i} className="flex gap-3 text-xs font-bold text-slate-500 dark:text-slate-400">
                      <div className="mt-1 w-1.5 h-1.5 rounded-full bg-indigo-600 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-black uppercase tracking-widest text-slate-900 dark:text-white flex items-center gap-3">
                <AlertCircle size={20} className="text-amber-600" /> {t.terms.liability.title}
              </h2>
              <p className="text-slate-600 dark:text-slate-400 leading-relaxed font-medium">
                {t.terms.liability.content}
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-xl font-black uppercase tracking-widest text-slate-900 dark:text-white flex items-center gap-3">
                <FileText size={20} className="text-purple-600" /> {t.terms.changes.title}
              </h2>
              <p className="text-slate-600 dark:text-slate-400 leading-relaxed font-medium">
                {t.terms.changes.content}
              </p>
            </section>
          </div>

          <div className="pt-10 border-t border-slate-200 dark:border-white/5 text-center">
             <p className="text-slate-400 text-xs font-medium">SharpNote &copy; 2026. All Rights Reserved.</p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Terms;